import React, { useEffect } from 'react';
import { EPButton } from '@/components';
import { ValidationDeviceStatus } from '@/store/InstallationValidation/constants';
import { useTranslation } from 'react-i18next';
import { CommentSection, ModalActions, StyledModal } from './CommentsModal.styles';
import { CommentModalsProps } from './CommentsModal.utils';

const CommentsModal: React.FC<CommentModalsProps> = ({
  isOpen,
  onClose,
  onSave,
  title,
  loadStatus,
  unloadStatus,
  flowStatus,
  initialLoadComment = '',
  initialUnloadComment = '',
  initialFlowComment = '',
  isSubmitted,
}) => {
  const { t } = useTranslation();
  const [loadComment, setLoadComment] = React.useState(initialLoadComment ?? '');
  const [unloadComment, setUnloadComment] = React.useState(initialUnloadComment ?? '');
  const [flowComment, setFlowComment] = React.useState(initialFlowComment ?? '');

  const isLoadFailed = loadStatus === ValidationDeviceStatus.FAILED;
  const isUnloadFailed = unloadStatus === ValidationDeviceStatus.FAILED;

  const isLoadNotTested = loadStatus === ValidationDeviceStatus.NOT_TESTED;
  const isUnloadNotTested = unloadStatus === ValidationDeviceStatus.NOT_TESTED;

  const shouldShowLoadComment = isLoadFailed || isLoadNotTested;
  const shouldShowUnloadComment = isUnloadFailed || isUnloadNotTested;

  const isFlowFailed = flowStatus === ValidationDeviceStatus.FAILED;
  const isFlowNotTested = flowStatus === ValidationDeviceStatus.NOT_TESTED;
  const shouldShowFlowComment = isFlowFailed || isFlowNotTested;

  useEffect(() => {
    setLoadComment(initialLoadComment ?? '');
    setUnloadComment(initialUnloadComment ?? '');
    setFlowComment(initialFlowComment ?? '');
  }, [initialLoadComment, initialUnloadComment, initialFlowComment]);

  const handleSave = () => {
    onSave(loadComment, unloadComment, flowComment);
    onClose();
  };

  return (
    <StyledModal open={isOpen} onCancel={onClose} footer={null} width={520} destroyOnClose={true} centered>
      <div className="modal-header">
        <div className="modal-header-text">{isSubmitted ? t('comment') : t('ADD_COMMENT')}</div>
      </div>
      <div className="modal-content">
        <div className="context-text">
          {t('commentsInfo')}
          <br />
          <br />
          <strong>{title}</strong>
        </div>

        {shouldShowLoadComment && (
          <CommentSection>
            <div className="label-container">
              <span className="required-mark">*</span>
              <span className="label">{isLoadFailed ? t('loadTestFailed') : t('loadTestNotTested')}</span>
            </div>
            <input
              className="comment-input"
              value={loadComment}
              onChange={e => setLoadComment(e.target.value)}
              placeholder={t('ADD_COMMENT')}
              disabled={isSubmitted}
            />
          </CommentSection>
        )}

        {shouldShowUnloadComment && (
          <CommentSection>
            <div className="label-container">
              <span className="required-mark">*</span>
              <span className="label">{isUnloadFailed ? t('unloadTestFailed') : t('unloadTestNotTested')}</span>
            </div>
            <input
              className="comment-input"
              value={unloadComment}
              onChange={e => setUnloadComment(e.target.value)}
              placeholder={t('ADD_COMMENT')}
              disabled={isSubmitted}
            />
          </CommentSection>
        )}

        {shouldShowFlowComment && (
          <CommentSection>
            <div className="label-container">
              <span className="required-mark">*</span>
              <span className="label">{isFlowFailed ? t('flowTestFailed') : t('flowTestNotTested')}</span>
            </div>
            <input
              className="comment-input"
              value={flowComment}
              onChange={e => setFlowComment(e.target.value)}
              placeholder={t('ADD_COMMENT')}
              disabled={isSubmitted}
            />
          </CommentSection>
        )}
      </div>
      <ModalActions>
        {!isSubmitted && (
          <>
            <EPButton onClick={onClose} type="secondary" className="cancel-button">
              {t('CANCEL')}
            </EPButton>
            <EPButton onClick={handleSave} type="primary">
              {t('okCamelCase')}
            </EPButton>
          </>
        )}
      </ModalActions>
    </StyledModal>
  );
};

export default CommentsModal;
